'use client';

import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from '@/components/atoms';
import { ActionButtons } from '@/components/molecules/ActionButtons';
import { PlayerAvailabilityTableSkeleton } from '@/components/molecules/skeletons/PlayerAvailabilityTableSkeleton';
import { ModalWrapper } from '@/components/organisms';
import { usePlayerAvailabilityQuery } from '@/hooks/player/usePlayerAvailabiltyQuery';
import { useRemovePlayerAvailabilityMutate, useUpdatePlayerActivityMutate } from '@/hooks/player/useUpdatePlayerActivityMutate';
import { TeamPlayerAvailabilityPayload } from '@/infrastructure/network/dto';
import { useOrganizationStore } from '@/store/organization-store';

const daysOfWeek = [
	{ key: 1, label: 'Mon' },
	{ key: 2, label: 'Tue' },
	{ key: 3, label: 'Wed' },
	{ key: 4, label: 'Thu' },
	{ key: 5, label: 'Fri' },
	{ key: 6, label: 'Sat' },
	{ key: 7, label: 'Sun' }
];
const timePeriods = [
	{ key: 4, label: 'All Day' },
	{ key: 1, label: 'Morn (6-11am)' },
	{ key: 2, label: 'Afternoon (12-5pm)' },
	{ key: 3, label: 'Evening (6-10pm)' }
];

enum TimeSlot {
	Morning = 1,
	Afternoon = 2,
	Evening = 3,
	AllDay = 4
}

interface PlayerAvailability {
	uuid?: string;
	playerUuid?: string;
	dayOfWeek: number;
	timeSlot: number;
	teamUuid: string;
}

interface PlayerAvailabilityTableProps {
	teamLeagueId: string;
	seasonId: string;
	playerId: string;
}

export const PlayerAvailabilityTable = ({ teamLeagueId, seasonId, playerId }: PlayerAvailabilityTableProps) => {
	const store = useOrganizationStore();
	const { data: playerAvailability, isFetching } = usePlayerAvailabilityQuery(teamLeagueId, playerId);
	const { mutate, isPending } = useUpdatePlayerActivityMutate();
	const [isEditing, setIsEditing] = useState(false);
	const availabilities: PlayerAvailability[] = playerAvailability?.data ?? [];

	if (isFetching || !playerAvailability) {
		return <PlayerAvailabilityTableSkeleton />;
	}
	if (playerAvailability?.statusCode !== 200) {
		return null;
	}

	const handleEdit = () => setIsEditing(true);

	const handleSave = (updatedAvailabilities: TeamPlayerAvailabilityPayload) => {
		const requestData = {
			organizationId: store.organizationUuid,
			teamLeagueId,
			seasonId,
			playerId,
			teamLeagueTeamPlayersAvailabilities: updatedAvailabilities
		};
		mutate(requestData, {
			onSuccess: () => {
				setIsEditing(false);
			}
		});
	};

	const toggleModal = () => {
		setIsEditing((prev) => !prev);
	};

	return (
		<>
			{!isFetching && (
				<div className="flex max-w-[560px] flex-col gap-4 bg-white">
					<div className="flex items-center justify-between">
						<h2 className="text-2xl font-semibold leading-8 text-darkorange-400">Availability</h2>
						<Button className="r self-end  px-4 py-2 !text-white bg-orange-500 hover:bg-orange-700" onClick={handleEdit}>
							Set availability
						</Button>
					</div>

					<table className="w-full text-center">
						<thead>
							<tr className="text-xs">
								<TableHeader />
							</tr>
						</thead>
						<tbody>
							{daysOfWeek.map((day) => (
								<tr key={day.key}>
									<td className="pr-4 text-end text-sm">{day.label}</td>
									{timePeriods.map((timePeriod, timeIndex) => {
										const widthClass = timeIndex === 2 || timeIndex === 3 ? 'w-[140px]' : 'w-[120px]';
										return (
											<td
												key={timePeriod.key}
												className={`h-7 border border-gray-300 px-5 text-sm ${widthClass} ${
													availabilities.some((a) => a.dayOfWeek === day.key && a.timeSlot === timePeriod.key)
														? 'bg-light-green text-white'
														: ''
												}`}
											></td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>

					{isEditing && (
						<ModalWrapper className="!max-w-[600px] !gap-0" title="Edit availability" onClose={toggleModal} closeOnOutsideClick={true}>
							<EditAvailabilityModal
								initialAvailabilities={availabilities}
								onSave={handleSave}
								onClose={toggleModal}
								isPending={isPending}
								organizationId={store.organizationUuid}
								teamLeagueId={teamLeagueId}
								playerId={playerId}
							/>
						</ModalWrapper>
					)}
				</div>
			)}
		</>
	);
};

const TableHeader = () => {
	return (
		<>
			<th className="p-2"></th>
			{timePeriods.map((period) => (
				<th key={period.key} className="p-2">
					{period.label}
				</th>
			))}
		</>
	);
};

const EditAvailabilityModal = ({
	initialAvailabilities,
	onSave,
	onClose,
	isPending,
	organizationId,
	teamLeagueId,
	playerId
}: {
	initialAvailabilities: PlayerAvailability[];
	onSave: (updatedAvailabilities: { teamLeagueTeamPlayersAvailabilities: { dayOfWeek: number; timeSlot: number; teamUuid: string }[] }) => void;
	onClose: () => void;
	isPending: boolean;
	organizationId: string;
	teamLeagueId: string;
	playerId: string;
}) => {
	const [editableAvailabilities, setEditableAvailabilities] = useState(initialAvailabilities);

	const { mutate: removeTeamPlayerAvailability } = useRemovePlayerAvailabilityMutate();

	const pathname = usePathname();
	const pathSegments = pathname.split('/');
	const teamUuidIndex = pathSegments.indexOf('team') + 1;
	const teamId = teamUuidIndex > 0 ? pathSegments[teamUuidIndex] : '';

	const toggleAvailability = (dayKey: number, timeKey: number) => {
		const teamUuid = teamId ?? '';

		setEditableAvailabilities((prev) =>
			prev.some((a) => a.dayOfWeek === dayKey && a.timeSlot === timeKey)
				? prev.filter((a) => a.dayOfWeek !== dayKey || a.timeSlot !== timeKey)
				: [...prev, { dayOfWeek: dayKey, timeSlot: timeKey, teamUuid }]
		);
	};

	const handleSubmit = () => {
		const teamUuid = teamId ?? '';

		const newAvailabilities = editableAvailabilities.filter(
			(newItem) =>
				!initialAvailabilities.some(
					(existingItem) => existingItem.dayOfWeek === newItem.dayOfWeek && existingItem.timeSlot === newItem.timeSlot
				)
		);

		const dayCounts = editableAvailabilities.reduce<Record<number, number>>((acc, { dayOfWeek }) => {
			acc[dayOfWeek] = (acc[dayOfWeek] || 0) + 1;
			return acc;
		}, {});

		const daysWithAllDay = new Set(editableAvailabilities.filter((a) => a.timeSlot === TimeSlot.AllDay).map((a) => a.dayOfWeek));

		if (editableAvailabilities.some((a) => daysWithAllDay.has(a.dayOfWeek) && a.timeSlot !== TimeSlot.AllDay)) {
			toast.error('Player is already marked as available for the entire day. You cannot select both "All Day" and specific time slots.');
			return;
		}

		const dayWithTooManySlots = Object.entries(dayCounts).find(([_, count]) => count > 2);
		if (dayWithTooManySlots) {
			toast.error('Conflict: You cannot have more than 2 time slots for one day.');
			return;
		}

		const deletedAvailabilities = initialAvailabilities
			.filter(
				(existingItem) =>
					!editableAvailabilities.some(
						(newItem) => newItem.dayOfWeek === existingItem.dayOfWeek && newItem.timeSlot === existingItem.timeSlot
					)
			)
			.map((item) => item.uuid)
			.filter((uuid): uuid is string => !!uuid);

		if (deletedAvailabilities.length > 0) {
			removeTeamPlayerAvailability(
				{ organizationId, teamLeagueId, playerId, availabilityUuids: deletedAvailabilities.join(',') },
				{ onSuccess: onClose }
			);
		}

		if (newAvailabilities.length > 0) {
			onSave({
				teamLeagueTeamPlayersAvailabilities: newAvailabilities.map(({ dayOfWeek, timeSlot }) => ({
					dayOfWeek,
					timeSlot,
					teamUuid
				}))
			});
		}

		return;
	};

	return (
		<div className="flex items-center justify-center">
			<div className="w-full bg-white">
				<h3 className="pb-2 pt-4 text-xl font-semibold text-gray-500">Choose the dates and times you are available for matches</h3>
				<table className="w-full text-center">
					<thead>
						<tr className="text-xs">
							<th className="p-2"></th>
							{timePeriods.map((period) => (
								<th key={period.key} className="p-2">
									{period.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{daysOfWeek.map((day) => (
							<tr key={day.key}>
								<td className="pr-4 text-end text-sm">{day.label}</td>
								{timePeriods.map((timePeriod, timeIndex) => {
									const widthClass = timeIndex === 2 || timeIndex === 3 ? 'w-[140px]' : 'w-[120px]';
									return (
										<td
											key={timePeriod.key}
											className={`${widthClass} cursor-pointer border border-gray-300 px-1 py-4 text-sm ${
												editableAvailabilities.some((a) => a.dayOfWeek === day.key && a.timeSlot === timePeriod.key)
													? 'bg-light-green text-white'
													: ''
											}`}
											onClick={() => toggleAvailability(day.key, timePeriod.key)}
										></td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
				<div className="mt-4 flex justify-end gap-4">
					<ActionButtons
						className="flex place-self-end !px-[10px] !py-[16px]"
						closeButtonClassName="w-full"
						submitButtonClassName="w-full !text-white bg-orange-500 hover:bg-orange-700"
						onClose={onClose}
						onSubmit={handleSubmit}
						submitButtonText="Confirm"
						closeButtonText="Cancel"
						submitButtonDisabled={isPending}
					/>
				</div>
			</div>
		</div>
	);
};
